// Global Instructions Rule Applied!

import {
  validateFilterOptions,
  type TableFilterOptionsResponse
} from '../../../state_manager/rpc_models/get_table_filter_options_response.ts'
import { ProspectRecord } from '../../../state_manager/interfaces/fundraising.ts'
import { validateProspectRecord, ProspectRecordFields } from '../../../state_manager/data_models/prospect_record.ts'
import { FilterValue, SortConfig } from '../../../state_manager/store_models/fundraising.ts'
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import { readBackendErrorMessage } from '../../../core/utils/utilities.ts'
import {
  validateBulkProspectCandidate,
  type BulkProspectCandidate,
  type BulkProspectResolvePayload,
  type BulkProspectResolveResponse,
  type BulkProspectCreatePayload,
  type BulkProspectCreateResponse
} from '../../../state_manager/data_models/bulk_prospect_candidates_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

/**
 * Normalizes a prospect record by adding `id` field from `prospects__id` for table display compatibility.
 * Also normalizes tasks field name from `tasks` to `prog__tasks` and ensures task objects have `tasks__subject`.
 * This ensures the record has both `prospects__id` (for API compatibility) and `id` (for table display).
 */
function normalizeProspectRecord(prospect: ProspectRecord): ProspectRecord {
  // Access prospects__id field directly since ProspectRecord allows [key: string]: any
  const prospectsId = (prospect as any)['prospects__id']
  const normalized: any = { ...prospect }

  // Always set id from prospects__id if prospects__id exists
  if (prospectsId) {
    normalized.id = prospectsId
  }

  // Normalize tasks field: if API returns 'tasks', move to 'prog__tasks' (correct field name per schema)
  if ((prospect as any)['tasks'] && Array.isArray((prospect as any)['tasks'])) {
    // Normalize task objects: ensure tasks__subject exists (map from tasks__title if needed)
    const normalizedTasks = ((prospect as any)['tasks'] as any[]).map((task: any) => {
      const normalizedTask = { ...task }
      // If tasks__subject is missing but tasks__title exists, use tasks__title as tasks__subject
      if (!normalizedTask['tasks__subject'] && normalizedTask['tasks__title']) {
        normalizedTask['tasks__subject'] = normalizedTask['tasks__title']
      }
      return normalizedTask
    })

    // Set prog__tasks (correct field name) and remove old 'tasks' field
    normalized['prog__tasks'] = normalizedTasks
    delete normalized['tasks']
  }

  // If prog__tasks exists, also normalize task objects within it
  if (normalized['prog__tasks'] && Array.isArray(normalized['prog__tasks'])) {
    normalized['prog__tasks'] = normalized['prog__tasks'].map((task: any) => {
      const normalizedTask = { ...task }
      // If tasks__subject is missing but tasks__title exists, use tasks__title as tasks__subject
      if (!normalizedTask['tasks__subject'] && normalizedTask['tasks__title']) {
        normalizedTask['tasks__subject'] = normalizedTask['tasks__title']
      }
      // If both are missing, set a default (though this shouldn't happen in normal operation)
      if (!normalizedTask['tasks__subject'] && !normalizedTask['tasks__title']) {
        normalizedTask['tasks__subject'] = 'Untitled Task'
      }
      return normalizedTask
    })
  }

  return normalized as ProspectRecord
}

/**
 * Normalizes an array of prospect records by adding `id` field from `prospects__id`.
 */
function normalizeProspectRecords(prospects: ProspectRecord[]): ProspectRecord[] {
  return prospects.map(normalizeProspectRecord)
}

export interface FundraisingPaginatedResponse {
  data: ProspectRecord[]
  hasMore: boolean
  totalCount: number
  error?: string
}

/**
 * Fetches prospects with pagination support for infinite scroll
 */
export const fetchFundraisingPaginated = async (
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null,
  reportId?: string | null
): Promise<FundraisingPaginatedResponse> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(
      buildFullUrl(
        BACKEND_BASE_URL,
        API_ROUTES.FUNDRAISING.LIST,
        {},
        {
          ...(pageSize !== null && { page: page.toString(), pageSize: pageSize.toString() }),
          ...(reportId ? { report_id: reportId } : {})
        }
      ),
      {
        method: API_ROUTES.FUNDRAISING.LIST.method,
        headers
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to fetch prospects: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown[]; has_more?: boolean; count?: number }

    if (parsed.data && Array.isArray(parsed.data) && parsed.data.length > 0) {
      const validatedData = validateProspectRecord(parsed.data, { isArray: true }) as ProspectRecord[]
      const normalizedData = normalizeProspectRecords(validatedData)
      return {
        data: normalizedData,
        hasMore: parsed.has_more || false,
        totalCount: parsed.count || 0
      }
    }

    return {
      data: [],
      hasMore: parsed.has_more || false,
      totalCount: parsed.count || 0
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Unexpected error fetching paginated prospects:', error)
    }
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching prospects'
    }
  }
}

/**
 * Fetches prospects with filtering and sorting using POST endpoint
 */
export const fetchFundraisingFiltered = async (
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null,
  reportId?: string | null,
  prospectQueryContext?: string,
  pipelineId?: string | null
): Promise<FundraisingPaginatedResponse> => {
  try {
    const sortConfigs = sorting ?? []
    const filterValues = filters ?? []

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const effectivePageSize = pageSize ?? 200

    const requestBody: {
      data: {
        sorting?: Array<{ key: string; direction: string }>
        filters?: Array<{ key: string; value: string[] | string | { min?: number | string; max?: number | string } }>
        prospect_query_context?: string
        pipeline_id?: string
        page?: number
        page_size?: number
        report_id?: string
      }
    } = {
      data: {
        page,
        page_size: effectivePageSize
      }
    }

    if (prospectQueryContext) {
      requestBody.data.prospect_query_context = prospectQueryContext
    }

    // Mobile pipeline switcher: pass the chosen Fundraising pipeline so the backend
    // scopes the table read to it (the backend validates + falls back to default).
    if (pipelineId) {
      requestBody.data.pipeline_id = pipelineId
    }

    requestBody.data.sorting = sortConfigs.map((sort) => ({
      key: sort.key,
      direction: sort.direction
    }))

    if (filterValues.length > 0) {
      requestBody.data.filters = filterValues.map((filter) => {
        // For contact relationship filters, extract UUIDs from contact objects if stored as objects
        const contactFilterKeys = [
          'contact_prospects__lead',
          'contact_accounts__introduced_by',
          'contact_accounts__sourced_by'
        ]

        let filterValue = filter.value

        if (contactFilterKeys.includes(filter.key) && Array.isArray(filterValue)) {
          // Check if it's an array of contact objects (with value and label)
          const isContactObjects =
            filterValue.length > 0 &&
            filterValue.every((item: any) => typeof item === 'object' && item !== null && item.value !== undefined)

          if (isContactObjects) {
            // Extract just the UUIDs (value field) for backend
            filterValue = filterValue.map((contact: any) => contact.value)
          }
        }

        return {
          key: filter.key,
          value: filterValue
        }
      })
    } else {
      requestBody.data.filters = []
    }

    if (reportId) {
      requestBody.data.report_id = reportId
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDRAISING.FILTER), {
      method: API_ROUTES.FUNDRAISING.FILTER.method,
      headers,
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to fetch filtered prospects: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown[]; has_more?: boolean; count?: number }

    if (parsed.data && Array.isArray(parsed.data) && parsed.data.length > 0) {
      const validatedData = validateProspectRecord(parsed.data, { isArray: true }) as ProspectRecord[]
      const normalizedData = normalizeProspectRecords(validatedData)
      return {
        data: normalizedData,
        hasMore: parsed.has_more || false,
        totalCount: parsed.count || 0
      }
    }

    return {
      data: [],
      hasMore: parsed.has_more || false,
      totalCount: parsed.count || 0
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Unexpected error fetching filtered prospects:', error)
    }
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching prospects'
    }
  }
}

export const fetchFundraisingFilterOptions = async (
  accessToken: string | null = null,
  requestedKeys?: string[]
): Promise<TableFilterOptionsResponse> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`

  const body: Record<string, unknown> = { data: {} }
  if (requestedKeys && requestedKeys.length > 0) {
    body.data = { requested_keys: requestedKeys }
  }

  const res = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDRAISING.FILTER_OPTIONS), {
    method: API_ROUTES.FUNDRAISING.FILTER_OPTIONS.method,
    headers,
    body: JSON.stringify(body)
  })

  if (!res.ok) {
    if (res.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(`Failed to fetch filter options: ${res.status} ${res.statusText}`)
  }

  const json: unknown = await res.json()
  const parsed = json as { data?: Record<string, unknown> }
  return { data: validateFilterOptions(parsed.data || {}) }
}

export interface FetchProspectByIdResponse {
  data: ProspectRecord | null
  error?: string
}

/**
 * Fetches a single prospect by ID
 */
export const fetchFundraisingById = async (
  prospectId: string | number,
  accessToken: string | null = null
): Promise<FetchProspectByIdResponse> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDRAISING.GET, { id: prospectId }), {
      method: API_ROUTES.FUNDRAISING.GET.method,
      headers
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      if (response.status === 404) {
        throw new Error('Prospect not found')
      }
      throw new Error(`Failed to fetch prospect: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown }

    if (parsed.data) {
      const validatedData = validateProspectRecord(parsed.data)
      const normalizedData = normalizeProspectRecord(validatedData as ProspectRecord)
      return {
        data: normalizedData
      }
    }

    return {
      data: null,
      error: 'No data returned from server'
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching prospect by ID:', error)
    }
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching prospect'
    }
  }
}

export const deleteProspect = async (prospectId: string | number, accessToken: string | null = null): Promise<void> => {
  const headers: Record<string, string> = {}
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }
  const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDRAISING.DELETE, { id: prospectId }), {
    method: API_ROUTES.FUNDRAISING.DELETE.method,
    headers
  })
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication failed - please log in again')
    }
    const errorBody = await response.json().catch(() => ({}))
    throw new Error((errorBody as { message?: string }).message || 'Failed to delete prospect')
  }
}

export interface CreateProspectResponse {
  data: ProspectRecord | null
  created_ids?: Record<string, string>
  error?: string
}

/**
 * Creates a new prospect with validation
 */
export const createFundraising = async (
  prospectData: Record<string, any>,
  accessToken: string | null = null
): Promise<CreateProspectResponse> => {
  try {
    // Filter to only include ProspectRecordFields constants (safety check)
    const validProspectFields = new Set<string>(Object.values(ProspectRecordFields))
    const cleanedData: Record<string, any> = {}

    Object.keys(prospectData).forEach((key) => {
      if (validProspectFields.has(key)) {
        cleanedData[key] = prospectData[key]
      }
    })

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDRAISING.LIST), {
      method: 'POST',
      headers,
      body: JSON.stringify(cleanedData)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      // The backend's own sentence (message / error), never a bare status line.
      throw new Error(await readBackendErrorMessage(response, 'Failed to create prospect'))
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown; created_ids?: Record<string, string> }

    if (parsed.data) {
      const validatedData = validateProspectRecord(parsed.data)
      const normalizedData = normalizeProspectRecord(validatedData as ProspectRecord)
      return {
        data: normalizedData,
        created_ids: parsed.created_ids
      }
    }

    return {
      data: null,
      error: 'No data returned from server'
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error creating prospect:', error)
    }
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while creating prospect'
    }
  }
}

export interface UpdateProspectResponse {
  data: ProspectRecord | null
  error?: string
}

/**
 * Updates an existing prospect
 * Note: For form field updates, InputManager auto-patch is preferred.
 * This function is kept for programmatic updates.
 */
export const updateFundraising = async (
  prospectId: string | number,
  prospectData: Record<string, any>,
  accessToken: string | null = null
): Promise<UpdateProspectResponse> => {
  try {
    // Filter to only include ProspectRecordFields constants (safety check)
    const validProspectFields = new Set<string>(Object.values(ProspectRecordFields))
    const cleanedData: Record<string, any> = {}

    Object.keys(prospectData).forEach((key) => {
      if (validProspectFields.has(key)) {
        cleanedData[key] = prospectData[key]
      }
    })

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDRAISING.UPDATE, { id: prospectId }), {
      method: API_ROUTES.FUNDRAISING.UPDATE.method,
      headers,
      body: JSON.stringify(cleanedData)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      if (response.status === 404) {
        throw new Error('Prospect not found')
      }
      if (response.status === 400) {
        const errorResult = await response.json()
        throw new Error((errorResult as { message?: string }).message || 'Invalid data provided')
      }
      throw new Error(`Failed to update prospect: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown }

    if (parsed.data) {
      const validatedData = validateProspectRecord(parsed.data)
      const normalizedData = normalizeProspectRecord(validatedData as ProspectRecord)
      return {
        data: normalizedData
      }
    }

    return {
      data: null,
      error: 'No data returned from server'
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error updating prospect:', error)
    }
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while updating prospect'
    }
  }
}

/**
 * Patches a pipeline_prospects row's stage (same call shape as the kanban
 * drag: bare columns, stage_date bumped to today).
 */
export const patchPipelineProspectStage = async (
  pipelineProspectsId: string,
  stageConfigId: string,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDRAISING.PIPELINE_PROSPECTS), {
      method: API_ROUTES.FUNDRAISING.PIPELINE_PROSPECTS.method,
      headers,
      body: JSON.stringify({
        id: pipelineProspectsId,
        stage: stageConfigId,
        stage_date: new Date().toISOString().split('T')[0]
      })
    })

    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string }
      return { success: false, error: body.message || `Failed to update stage: ${response.status}` }
    }
    return { success: true }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while updating the stage'
    }
  }
}

/** Patches a prospect's commitment (bare-column PATCH /prospects). */
export const patchProspectCommitment = async (
  prospectId: string,
  commitment: number | null,
  accessToken: string | null = null
): Promise<{ success: boolean; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDRAISING.PATCH), {
      method: API_ROUTES.FUNDRAISING.PATCH.method,
      headers,
      body: JSON.stringify({ id: prospectId, commitment })
    })

    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: string }
      return { success: false, error: body.message || `Failed to update commitment: ${response.status}` }
    }
    return { success: true }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while updating the commitment'
    }
  }
}

export interface SearchFundraisingForPopupResponse {
  data: ProspectRecord[]
  error?: string
}

/**
 * Searches prospects for toolbar search popup (independent of table filters)
 * @param searchTerm - The search term to look for
 * @param limit - Maximum number of results to return (default: 10)
 * @param accessToken - User's access token from Redux store
 * @returns Promise with validated prospect records or error
 */
export const searchFundraisingForPopup = async (
  searchTerm: string,
  limit: number = 10,
  accessToken: string | null = null
): Promise<SearchFundraisingForPopupResponse> => {
  try {
    if (!searchTerm || searchTerm.trim() === '') {
      return { data: [] }
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const queryParams = new URLSearchParams({
      search: searchTerm.trim(),
      limit: limit.toString()
    })

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDRAISING.SEARCH, {}, Object.fromEntries(queryParams)),
      {
        method: API_ROUTES.FUNDRAISING.SEARCH.method,
        headers
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to search prospects: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown[] }

    if (parsed.data) {
      const limitedData = parsed.data.slice(0, limit)
      const validatedData = validateProspectRecord(limitedData, { isArray: true }) as ProspectRecord[]
      return {
        data: validatedData
      }
    }

    return {
      data: []
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error searching prospects for popup:', error)
    }
    return {
      data: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred while searching prospects'
    }
  }
}

/**
 * Compatibility wrapper for createProspect from deprecated prospectsService
 * Converts the old service signature to the new API format
 * Compatible with old fundraising_deprecated service
 * Note: This function expects the caller to pass accessToken from Redux state
 * @param prospectData - Prospect data in old format
 * @param accessToken - User's access token (should be passed from Redux state)
 * @returns Promise with old format response: { success: boolean, data?: ProspectRecord | null, error?: string, createdAccountId?: string }
 */
export const createProspect = async (
  prospectData: Record<string, any>,
  accessToken: string | null = null
): Promise<{ success: boolean; data?: ProspectRecord | null; error?: string; createdAccountId?: string | null }> => {
  try {
    const response = await createFundraising(prospectData, accessToken)

    if (response.error) {
      return {
        success: false,
        error: response.error
      }
    }

    // Extract created account ID from created_ids if available
    const createdAccountId = response.created_ids?.account_id || response.created_ids?.accounts__id || null

    return {
      success: true,
      data: response.data,
      createdAccountId
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error creating prospect (compatibility wrapper):', error)
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while creating prospect'
    }
  }
}

// ── Bulk Add Prospects (bulk-create prospects for existing LPs) ─────────────

/**
 * Resolve the candidate LP accounts for the Bulk Add Prospects screen.
 * POST /prospects/bulk-candidates → view__bulk_prospect_candidates.
 * Every row is Zod-validated before it reaches the screen; a row that fails
 * is dropped with a dev warning rather than failing the whole resolution
 * (the resolveDistributionList idiom).
 */
export const fetchBulkProspectCandidates = async (
  payload: BulkProspectResolvePayload,
  accessToken: string | null = null
): Promise<BulkProspectResolveResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDRAISING.BULK_CANDIDATES), {
      method: API_ROUTES.FUNDRAISING.BULK_CANDIDATES.method,
      headers,
      body: JSON.stringify({ data: payload })
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      throw new Error(`Failed to resolve candidate LPs: ${response.status} ${response.statusText}`)
    }

    const json = (await response.json()) as { data?: unknown[] }
    const rawRows = Array.isArray(json.data) ? json.data : []

    const validated: BulkProspectCandidate[] = []
    for (const row of rawRows) {
      try {
        validated.push(validateBulkProspectCandidate(row))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Bulk prospect candidate row failed validation:', e, row)
        }
      }
    }

    return { data: validated }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error resolving bulk prospect candidates:', error)
    }
    return {
      data: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred while resolving candidate LPs'
    }
  }
}

/**
 * Atomically create prospects for many LP accounts in one fund.
 * POST /prospects/bulk-create — the whole batch lands or nothing does.
 * A 409 means some rows collided with live prospects that appeared between
 * resolve and submit; conflictAccountIds carries the offenders so the screen
 * can re-resolve and refresh its flags.
 */
export const bulkCreateProspects = async (
  payload: BulkProspectCreatePayload,
  accessToken: string | null = null
): Promise<BulkProspectCreateResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.FUNDRAISING.BULK_CREATE), {
      method: API_ROUTES.FUNDRAISING.BULK_CREATE.method,
      headers,
      body: JSON.stringify({ data: payload })
    })

    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      let message = `Failed to create prospects: ${response.status} ${response.statusText}`
      let conflictAccountIds: string[] | undefined
      try {
        const body = (await response.json()) as { message?: string; conflict_account_ids?: string[] }
        if (body?.message) message = body.message
        if (Array.isArray(body?.conflict_account_ids)) conflictAccountIds = body.conflict_account_ids
      } catch {
        // non-JSON error body — keep the status message
      }
      return { data: null, error: message, conflictAccountIds }
    }

    const json = (await response.json()) as {
      data?: { created_count?: number; prospect_ids?: string[] }
    }
    return {
      data: {
        created_count: json.data?.created_count ?? 0,
        prospect_ids: json.data?.prospect_ids ?? []
      }
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error bulk-creating prospects:', error)
    }
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while creating prospects'
    }
  }
}
